import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { PlayIcon } from "@heroicons/react/outline";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import Checkbox from "../../../common/components/Input/Checkbox";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  closeConfirmationModal,
  getShowStandardModal,
  openConfirmationModal,
} from "../../../common/state/slice/modal/modalSlice";
import { useHelpLauncherBottom } from "../../../common/util/helpDocsLighthouse/useHelpLauncherBottom";
import { TourResumePill } from "../engine/TourResumePill";
import { useHelpLauncherBox } from "../hooks/useHelpLauncherBox";
import { useRouteNeedsUnloadedEntity } from "../hooks/useRouteNeedsUnloadedEntity";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import { getStepPosition, getTour } from "../registry";
import type { ResumableTour } from "../state/onboardingSlice";
import {
  SESSION_STARTED_AT_MS,
  applyOnboardingOps,
  findChecklistItemForTour,
  getChecklistProgress,
  selectActiveTour,
  selectOnboardingAudience,
  selectOnboardingRemote,
  selectOnboardingStatus,
  selectSuggestionsMuted,
} from "../state/onboardingSlice";
import { ChecklistCard } from "./ChecklistCard";

export const CHECKLIST_DISMISS_MODAL_ID = "onboarding-checklist-dismiss";

const LAUNCHER_PULSE_MS = 6000;
const MUTE_SUGGESTIONS_CHECKBOX_ID = "onboarding-checklist-dismiss-mute";

const RING_RADIUS = 9;
const LAUNCHER_PILL_HEIGHT = 40;
const HELP_LAUNCHER_GAP = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ percentage }: { percentage: number }) => (
  <svg className="h-6 w-6 -rotate-90 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <circle
      cx="12"
      cy="12"
      r={RING_RADIUS}
      fill="none"
      strokeWidth="3"
      className="stroke-white/20"
    />
    <circle
      cx="12"
      cy="12"
      r={RING_RADIUS}
      fill="none"
      strokeWidth="3"
      strokeLinecap="round"
      className="stroke-green-500 transition-[stroke-dashoffset] duration-500"
      strokeDasharray={RING_CIRCUMFERENCE}
      strokeDashoffset={RING_CIRCUMFERENCE * (1 - percentage / 100)}
    />
  </svg>
);

export type ChecklistLauncherProps = {
  resumable?: ResumableTour;
};

export const ChecklistLauncher = ({ resumable }: ChecklistLauncherProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectOnboardingStatus);
  const audience = useAppSelector(selectOnboardingAudience);
  const remote = useAppSelector(selectOnboardingRemote);
  const activeTour = useAppSelector(selectActiveTour);
  const suggestionsMuted = useAppSelector(selectSuggestionsMuted);
  const showStandardModal = useAppSelector(getShowStandardModal);
  const helpLauncher = useHelpLauncherBox();
  const helpLauncherBottom = useHelpLauncherBottom();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [muteOnDismiss, setMuteOnDismiss] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const autoOpenedRef = useRef(false);

  const progress = getChecklistProgress(remote, audience);
  const completedBeforeSession =
    progress?.completedAtMs !== null &&
    progress?.completedAtMs !== undefined &&
    progress.completedAtMs < SESSION_STARTED_AT_MS;
  const launcherVisible =
    Boolean(progress) &&
    !suggestionsMuted &&
    !remote?.checklist.dismissedAt &&
    !activeTour &&
    !completedBeforeSession &&
    resumable?.route !== location.pathname;
  const launcherSeenAt = remote?.checklist.launcherSeenAt ?? null;
  const linkedItem = resumable ? findChecklistItemForTour(progress, resumable.id) : null;
  const resumableTour = resumable ? getTour(resumable.id, audience) : undefined;
  const mergedResume = Boolean(resumable && launcherVisible && linkedItem && resumableTour);
  const needsRestart = useRouteNeedsUnloadedEntity();
  const resumeRestarts = resumable ? needsRestart(resumable.route) : false;
  const launcherAriaLabel =
    mergedResume && progress && resumable && resumableTour
      ? t(
          resumeRestarts
            ? "onboarding.checklist.launcherRestartAriaLabel"
            : "onboarding.checklist.launcherResumeAriaLabel",
          {
            title: t(progress.checklist.titleKey),
            tour: t(resumableTour.titleKey),
            ...getStepPosition(resumableTour, resumable.step),
          },
        )
      : undefined;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setPulsing(false);
      }
      if (nextOpen && !launcherSeenAt) {
        void dispatch(applyOnboardingOps([{ op: "checklistLauncherSeen" }]));
      }
    },
    [dispatch, launcherSeenAt],
  );

  const { refs, floatingStyles, context } = useFloating({
    open: open && launcherVisible,
    onOpenChange: handleOpenChange,
    placement: "top-end",
    middleware: [offset(12), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context)]);
  const setSlotNode = useCallback(
    (node: HTMLDivElement | null) => refs.setPositionReference(node),
    [refs],
  );
  const setLauncherNode = useCallback(
    (node: HTMLButtonElement | null) => refs.setReference(node),
    [refs],
  );
  const setPopoverNode = useCallback(
    (node: HTMLDivElement | null) => refs.setFloating(node),
    [refs],
  );

  useEffect(() => {
    if (autoOpenedRef.current || launcherSeenAt) {
      return undefined;
    }
    if (status !== "ready" || !remote || remote.welcome.seenAt === null) {
      return undefined;
    }
    if (!launcherVisible || showStandardModal.show) {
      return undefined;
    }
    autoOpenedRef.current = true;
    setPulsing(true);
    void dispatch(applyOnboardingOps([{ op: "checklistLauncherSeen" }]));
    const timer = window.setTimeout(() => setPulsing(false), LAUNCHER_PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [status, remote, launcherSeenAt, launcherVisible, showStandardModal.show, dispatch]);

  useEffect(() => {
    if (activeTour) {
      setOpen(false);
    }
  }, [activeTour]);

  const handleRequestDismiss = (): void => {
    setOpen(false);
    setMuteOnDismiss(false);
    dispatch(openConfirmationModal(CHECKLIST_DISMISS_MODAL_ID));
  };

  const handleConfirmDismiss = (): void => {
    dispatch(closeConfirmationModal(CHECKLIST_DISMISS_MODAL_ID));
    void dispatch(
      applyOnboardingOps(
        muteOnDismiss
          ? [{ op: "checklistDismiss" }, { op: "suggestionsMute" }]
          : [{ op: "checklistDismiss" }],
      ),
    );
    enqueueOnboardingEvent({
      event: "checklist_dismissed",
      surface: "checklist",
      itemId: null,
      step: null,
    });
    if (muteOnDismiss) {
      enqueueOnboardingEvent({
        event: "suggestions_muted",
        surface: "checklist",
        itemId: null,
        step: null,
      });
    }
  };

  const slotStyle = helpLauncher
    ? {
        right: `${window.innerWidth - helpLauncher.left + HELP_LAUNCHER_GAP}px`,
        bottom: `${
          window.innerHeight -
          helpLauncher.bottom +
          (helpLauncher.height - LAUNCHER_PILL_HEIGHT) / 2
        }px`,
      }
    : { bottom: `${helpLauncherBottom}px` };
  const slotFallbackClass = helpLauncher ? "" : "right-6";

  const percentage =
    progress && progress.total > 0 ? Math.round((progress.doneCount / progress.total) * 100) : 0;

  return (
    <>
      {((resumable && !suggestionsMuted) || launcherVisible) && (
        <FloatingPortal>
          <div
            ref={setSlotNode}
            data-testid="onboarding-launcher-slot"
            className={`fixed ${slotFallbackClass} z-[1050] flex flex-col items-end gap-2`}
            style={slotStyle}
          >
            {resumable && !mergedResume && !suggestionsMuted && (
              <TourResumePill resumable={resumable} />
            )}
            {launcherVisible && progress && (
              <button
                ref={setLauncherNode}
                type="button"
                data-testid="onboarding-checklist-launcher"
                data-resume={mergedResume ? "true" : undefined}
                data-pulsing={pulsing ? "true" : undefined}
                aria-expanded={open}
                aria-label={launcherAriaLabel}
                className="relative flex items-center gap-2 h-10 pl-2 pr-4 rounded-full bg-blue-500 shadow-lg hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
                {...getReferenceProps({ onClick: () => handleOpenChange(!open) })}
              >
                {pulsing && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full bg-blue-500 opacity-75 motion-safe:animate-ping"
                  />
                )}
                {mergedResume ? (
                  <PlayIcon className="h-6 w-6 shrink-0 text-green-400" aria-hidden="true" />
                ) : (
                  <ProgressRing percentage={percentage} />
                )}
                <span className="text-sm font-semibold text-white">
                  {t(progress.checklist.titleKey)}
                </span>
                <span className="text-sm text-white/70">
                  {progress.doneCount}/{progress.total}
                </span>
              </button>
            )}
          </div>
        </FloatingPortal>
      )}
      {open && launcherVisible && progress && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
            <div
              ref={setPopoverNode}
              role="dialog"
              aria-label={t(progress.checklist.titleKey)}
              data-testid="onboarding-checklist-popover"
              className="z-[1050] bg-white rounded-xl shadow-xl ring-1 ring-slate-900/5 w-[360px] overflow-hidden focus:outline-none"
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <ChecklistCard
                progress={progress}
                resumable={mergedResume ? resumable : undefined}
                onCollapse={() => setOpen(false)}
                onRequestDismiss={handleRequestDismiss}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
      <ModalConfirmation
        id={CHECKLIST_DISMISS_MODAL_ID}
        title={t("onboarding.checklist.dismissConfirmTitle")}
        text={t("onboarding.checklist.dismissConfirmText")}
        submitButtonText={t("general.hide")}
        onClickSubmit={handleConfirmDismiss}
      >
        <Checkbox
          id={MUTE_SUGGESTIONS_CHECKBOX_ID}
          label={t("onboarding.checklist.dismissMuteSuggestions")}
          checked={muteOnDismiss}
          onChange={(event) => setMuteOnDismiss(event.target.checked)}
        />
      </ModalConfirmation>
    </>
  );
};

export default ChecklistLauncher;
